/**
 * Building catalogue (GDD §1 "Gebäude-Katalog").
 *
 * `baseCost` is the level-1 price; every further level scales by `l^COST_EXP`
 * (formulas.ts). `production` names which resource a mine yields.
 *
 * Base values are design fill-ins, tuned with `scripts/tune-curve.mjs` so a
 * fresh colony finishes its first mine in seconds, reaches level 5 in ~17
 * minutes, level 100 in ~3 days and level 1000 after ~5 years.
 */

import type { BuildingKey, ResearchKey, ResourceAmounts, StoredResource } from '../types.js';

export interface BuildingDef {
  key: BuildingKey;
  label: string;
  description: string;
  /**
   * Cost at level 1. Every further level scales by `l^COST_EXP`
   * (see formulas.ts) — buildings use the polynomial curve so mine levels can
   * reach the high hundreds.
   */
  baseCost: ResourceAmounts;
  /** Which stored resource this building produces, if any. */
  production?: { resource: Extract<StoredResource, 'titan' | 'silicate' | 'isotope'>; basePerHour: number };
  /** Produces energy instead of a stored resource. */
  energy?: boolean;
  /** Raises the storage cap of this resource (see formulas.ts storageCapacity). */
  storage?: Extract<StoredResource, 'titan' | 'silicate' | 'isotope'>;
  /** Where it can be built. Defaults to the planet. */
  location?: 'planet' | 'moon';
  requires?: Partial<{ buildings: Partial<Record<BuildingKey, number>>; research: Partial<Record<ResearchKey, number>> }>;
  /** Sprite id in @spacegame/assets. */
  sprite: string;
}

export const BUILDINGS: Record<BuildingKey, BuildingDef> = {
  titanSmelter: {
    key: 'titanSmelter',
    label: 'Titan-Schmelze',
    description: 'Fördert Titan-Legierung durch Tiefbohrung. Skaliert exponentiell im Energiebedarf.',
    baseCost: { titan: 40, silicate: 12 },
    production: { resource: 'titan', basePerHour: 600 },
    sprite: 'building-titan-smelter',
  },
  silicateRefinery: {
    key: 'silicateRefinery',
    label: 'Silikat-Raffinerie',
    description: 'Reinigt Kristall-Silikat für Hochtechnologie, Schildgeneratoren und Computer-Kerne.',
    baseCost: { titan: 32, silicate: 20 },
    production: { resource: 'silicate', basePerHour: 380 },
    sprite: 'building-silicate-refinery',
  },
  isotopeCondenser: {
    key: 'isotopeCondenser',
    label: 'Isotop-Kondensator',
    description: 'Filtert Isotop-3 aus der Atmosphäre. Der Ertrag ist auf kälteren Planeten höher.',
    baseCost: { titan: 160, silicate: 60 },
    production: { resource: 'isotope', basePerHour: 140 },
    sprite: 'building-isotope-condenser',
  },
  solarPlant: {
    key: 'solarPlant',
    label: 'Solar-Gravitations-Kraftwerk',
    description: 'Wandelt Strahlung in Megawatt-Energie um und bestimmt die Effizienz aller Minen.',
    baseCost: { titan: 50, silicate: 20 },
    energy: true,
    sprite: 'building-solar-plant',
  },
  researchLab: {
    key: 'researchLab',
    label: 'Quanten-Forschungslabor',
    description: 'Verkürzt Forschungszeiten und schaltet Technologien frei.',
    baseCost: { titan: 800, silicate: 1_600, isotope: 400 },
    sprite: 'building-research-lab',
  },
  tradeRelay: {
    key: 'tradeRelay',
    label: 'Händler-Relaisstation',
    description: 'Schaltet ab Stufe 1 das Händler-Portal frei und senkt Transaktionssteuern.',
    baseCost: { titan: 600, silicate: 1_200, credit: 200 },
    sprite: 'building-trade-relay',
  },
  /* Owner decision, no GDD section — closes a credit dead-end: without this
   * requirement a player could gamble away their starting credits before
   * ever building tradeRelay, leaving them with no way to earn more (the
   * market itself needs tradeRelay). Requiring it here guarantees every
   * casino-capable colony already has a credit source. Levels gate which
   * games are playable — see CASINO_GAME_MIN_LEVEL in casino.ts. */
  casino: {
    key: 'casino',
    label: 'Casino',
    description: 'Schaltet Glücksspiele frei — jede Stufe eröffnet ein weiteres Spiel.',
    baseCost: { titan: 2_000, silicate: 4_000, credit: 500 },
    requires: { buildings: { tradeRelay: 1 } },
    sprite: 'building-casino',
  },
  /* Owner decision, no GDD section (2026-08-01) — ships had no building
   * gate at all before this, only research prerequisites (data/units.ts).
   * Level requirements per ship live there; this building itself has no
   * requires so it's buildable from the start, same tier as the other
   * early-game specialist buildings. */
  shipyard: {
    key: 'shipyard',
    label: 'Werft',
    description: 'Ermöglicht den Bau von Schiffen. Höhere Stufen schalten größere Schiffsklassen frei.',
    baseCost: { titan: 400, silicate: 200 },
    sprite: 'building-shipyard',
  },
  /* Owner decision, no GDD section (2026-08-01) — caps simultaneous fleet
   * missions (state.expedition + state.harvest today, state.fleetMissions[]
   * once Meilenstein F lands), not raw ship count. See
   * formulas.ts fleetHangarCapacity() for the capacity curve and the
   * reasoning behind it. */
  shipHangar: {
    key: 'shipHangar',
    label: 'Schiffshangar',
    description:
      'Begrenzt die Anzahl gleichzeitig unterwegs befindlicher Flotten. Niedrige Stufen bringen die meisten zusätzlichen Plätze.',
    baseCost: { titan: 500, silicate: 300 },
    sprite: 'building-ship-hangar',
  },
  nanoFactory: {
    key: 'nanoFactory',
    label: 'Nano-Fabrikationszentrum',
    description: 'Halbiert die Bauzeit von Gebäuden und Schiffen pro Stufe.',
    baseCost: { titan: 250_000, silicate: 120_000, isotope: 40_000 },
    requires: { buildings: { titanSmelter: 10 } },
    sprite: 'building-nano-factory',
  },
  phalanx: {
    key: 'phalanx',
    label: 'Subraum-Phalanx',
    description:
      'Scannt Flottenbewegungen im Sektor vom Basis-Mond aus. Grundlage für Push-Warnungen und das Frühwarnsystem.',
    baseCost: { titan: 40_000, silicate: 80_000, isotope: 40_000 },
    location: 'moon',
    sprite: 'building-phalanx',
  },
  missileSilo: {
    key: 'missileSilo',
    label: 'Interkontinental-Silobasis',
    description:
      'Produziert und startet Abfang- und Schlagraketen gegen Kometen und feindliche Basen.',
    baseCost: { titan: 12_000, silicate: 4_000, isotope: 1_000 },
    sprite: 'building-missile-silo',
  },

  /* Storage buildings — design fill-in on owner request (the GDD defines no
   * storage; capacities live in formulas.ts `BASE_STORAGE`/`storageCapacity`).
   * Capacity grows as (1+level)^1.9 — just above the production exponent, so
   * storage stays a chore you keep up with rather than a wall. */
  titanDepot: {
    key: 'titanDepot',
    label: 'Titan-Depot',
    description:
      'Unterirdisches Hochdrucklager für Titan-Legierung. Jede Stufe erhöht die Lagerkapazität deutlich.',
    baseCost: { titan: 400, silicate: 100 },
    storage: 'titan',
    sprite: 'building-titan-depot',
  },
  silicateVault: {
    key: 'silicateVault',
    label: 'Silikat-Speicher',
    description:
      'Abgeschirmte Kristallkammern für Kristall-Silikat. Jede Stufe erhöht die Lagerkapazität deutlich.',
    baseCost: { titan: 400, silicate: 200 },
    storage: 'silicate',
    sprite: 'building-silicate-vault',
  },
  isotopeTank: {
    key: 'isotopeTank',
    label: 'Isotop-Tank',
    description:
      'Kryogener Drucktank für Isotop-3. Jede Stufe erhöht die Lagerkapazität deutlich.',
    baseCost: { titan: 400, silicate: 400 },
    storage: 'isotope',
    sprite: 'building-isotope-tank',
  },
};

export const BUILDING_KEYS = Object.keys(BUILDINGS) as BuildingKey[];
